/**
 * Home health demo store — localStorage persistence.
 *
 * Versioned key: a schema bump re-seeds instead of migrating. The real-data
 * snapshot (`server`) and the optimistic overlay (`local`) are never written. The envelope carries no timestamp so two
 * tabs holding identical state write identical strings and the browser fires no
 * `storage` event (prevents cross-tab ping-pong).
 */
import { DEMO_SCHEMA_VERSION, EMPTY_LOCAL_OVERLAY, EMPTY_SERVER_SNAPSHOT, type DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";

export const STORAGE_KEY_PREFIX = "home-health-demo";
export const ENABLED_KEY = `${STORAGE_KEY_PREFIX}:enabled`;

export const storageKey = (version: number = DEMO_SCHEMA_VERSION) => `${STORAGE_KEY_PREFIX}:v${version}`;

export type PersistedState = Omit<DemoState, "server" | "local">;

interface Envelope {
  schemaVersion: number;
  state: PersistedState;
}

export function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function stripTransient(state: DemoState): PersistedState {
  const { server: _server, local: _local, ...rest } = state;
  return rest;
}

export function serialize(state: DemoState): string {
  const envelope: Envelope = { schemaVersion: state.meta.schemaVersion, state: stripTransient(state) };
  return JSON.stringify(envelope);
}

export function deserialize(raw: string | null | undefined, expectedVersion: number = DEMO_SCHEMA_VERSION): DemoState | undefined {
  if (!raw) return undefined;
  try {
    const envelope = JSON.parse(raw) as Partial<Envelope>;
    if (!envelope || typeof envelope !== "object") return undefined;
    if (envelope.schemaVersion !== expectedVersion) return undefined;
    const state = envelope.state;
    if (!state || typeof state !== "object" || !state.meta || state.meta.schemaVersion !== expectedVersion) return undefined;
    return { ...state, server: EMPTY_SERVER_SNAPSHOT, local: EMPTY_LOCAL_OVERLAY };
  } catch {
    return undefined;
  }
}

export function loadPersisted(storage: Storage | undefined = safeLocalStorage(), version: number = DEMO_SCHEMA_VERSION): DemoState | undefined {
  if (!storage) return undefined;
  try {
    return deserialize(storage.getItem(storageKey(version)), version);
  } catch {
    return undefined;
  }
}

export function savePersisted(state: DemoState, storage: Storage | undefined = safeLocalStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(storageKey(state.meta.schemaVersion), serialize(state));
  } catch {
    // Quota or privacy mode: the demo keeps running in memory.
  }
}

export function clearPersisted(storage: Storage | undefined = safeLocalStorage(), version: number = DEMO_SCHEMA_VERSION): void {
  try {
    storage?.removeItem(storageKey(version));
  } catch {
    // ignore
  }
}

export interface DebouncedSaver {
  schedule(state: DemoState): void;
  flush(): void;
  cancel(): void;
}

export function createDebouncedSaver(save: (state: DemoState) => void, delayMs = 250): DebouncedSaver {
  let pending: DemoState | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    if (pending) {
      const toSave = pending;
      pending = undefined;
      save(toSave);
    }
  };
  return {
    schedule(state) {
      pending = state;
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, delayMs);
    },
    flush,
    cancel() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      pending = undefined;
    },
  };
}

/** Notify when another tab writes our key. Same-tab writes do not fire `storage`. */
export function subscribeToExternalChanges(
  onState: (state: DemoState) => void,
  version: number = DEMO_SCHEMA_VERSION,
): () => void {
  if (typeof window === "undefined") return () => {};
  const key = storageKey(version);
  const handler = (event: StorageEvent) => {
    if (event.key !== key) return;
    const state = deserialize(event.newValue, version);
    if (state) onState(state);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
